import { FirebaseServices } from "./config";
import { callFunction } from "./helpers";
import { entityCache } from "../sync/entity-cache";
import {
  CreatePlaceRequest,
  CreatePlaceResponse,
  EditPlaceRequest,
  EditPlaceResponse,
  DeletePlaceRequest,
  DeletePlaceResponse,
  GetPlaceRequest,
  GetPlaceResponse,
  GetMyPlacesRequest,
  GetMyPlacesResponse,
  GetFilteredPlacesRequest,
  GetFilteredPlacesResponse,
  GetAllPlacesRequest,
  GetAllPlacesResponse,
} from "common247";

/**
 * Creates a new Place entity.
 *
 * @param fb - Firebase services instance.
 * @param request - Payload with name, description, address, coordinates, etc.
 * @returns Server response with created placeId.
 */
export async function createPlace(
  fb: FirebaseServices,
  request: CreatePlaceRequest
): Promise<CreatePlaceResponse> {
  const result = (await callFunction(fb, "createPlaceFunc", request)) as CreatePlaceResponse;
  return result;
}

/**
 * Modifies an existing Place entity.
 *
 * @param fb - Firebase services instance.
 * @param request - Payload with placeId and mutated attributes.
 * @returns Standard server response.
 */
export async function editPlace(
  fb: FirebaseServices,
  request: EditPlaceRequest
): Promise<EditPlaceResponse> {
  const result = (await callFunction(fb, "editPlaceFunc", request)) as EditPlaceResponse;
  return result;
}

/**
 * Deletes a Place entity.
 *
 * @param fb - Firebase services instance.
 * @param request - Payload with placeId.
 * @returns Standard server response.
 */
export async function deletePlace(
  fb: FirebaseServices,
  request: DeletePlaceRequest
): Promise<DeletePlaceResponse> {
  const result = (await callFunction(fb, "deletePlaceFunc", request)) as DeletePlaceResponse;
  return result;
}

/**
 * Retrieves a single Place entity by ID.
 *
 * @param fb - Firebase services instance.
 * @param request - Payload with placeId.
 * @returns Server response with place document.
 */
export async function getPlace(
  fb: FirebaseServices,
  request: GetPlaceRequest
): Promise<GetPlaceResponse> {
  const result = (await callFunction(fb, "getPlaceFunc", request)) as GetPlaceResponse;
  return result;
}

/**
 * Retrieves all places owned by or shared with the caller.
 *
 * @param fb - Firebase services instance.
 * @param request - Optional query options.
 * @returns Server response with places array.
 */
export async function getMyPlaces(
  fb: FirebaseServices,
  request: GetMyPlacesRequest
): Promise<GetMyPlacesResponse> {
  const result = (await callFunction(fb, "getMyPlacesFunc", request)) as GetMyPlacesResponse;
  if (result && Array.isArray(result.places)) {
    entityCache.setCachedList("place" as any, result.places, "myPlaces");
  }
  return result;
}

/**
 * Retrieves filtered places matching unified search criteria.
 *
 * @param fb - Firebase services instance.
 * @param request - Unified filter criteria payload.
 * @returns Server response with filtered places and pagination metadata.
 */
export async function getFilteredPlaces(
  fb: FirebaseServices,
  request: GetFilteredPlacesRequest
): Promise<GetFilteredPlacesResponse> {
  const result = (await callFunction(fb, "getFilteredPlacesFunc", request)) as GetFilteredPlacesResponse;
  return result;
}

/**
 * Admin function to retrieve all places in the system.
 *
 * @param fb - Firebase services instance.
 * @param request - Optional filter parameters.
 * @returns Server response with all places across all owners.
 */
export async function getAllPlaces(
  fb: FirebaseServices,
  request: GetAllPlacesRequest = {}
): Promise<GetAllPlacesResponse> {
  const result = (await callFunction(fb, "getAllPlacesFunc", request)) as GetAllPlacesResponse;
  return result;
}

